import React from "react";

import {
  faCheckCircle,
  faCircleNotch,
  faExclamationCircle,
  faExclamationTriangle,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import toast, { ToastBar } from "react-hot-toast";

import { Icon, IconButton } from "src/design-system/Icon";
import {
  ContentProps,
  NotificationVariant,
  Props,
  ToastMultiStateItem,
} from "src/design-system/Toast/constants";
import { useTrackNotification } from "src/design-system/Toast/utils";
import { ClampedContent } from "src/design-system/utils/ClampedContent";
import { assertUnreachable } from "src/utils/typeUtils";

export const RESET_TOASTBAR_STYLES: React.CSSProperties = {
  padding: 0,
  boxShadow: "unset",
  borderRadius: 0,
  background: "transparent",
};

export const ToastIcon: React.FC<{ variant: NotificationVariant }> = ({
  variant,
}) => {
  switch (variant) {
    case "default":
      return null;
    case "success":
      return <Icon icon={faCheckCircle} size="md" variant="success" />;
    case "error":
      return <Icon icon={faExclamationCircle} size="md" variant="error" />;
    case "warning":
      return <Icon icon={faExclamationTriangle} size="md" variant="warning" />;
    case "loading":
      return <Icon icon={faCircleNotch} size="md" variant="active" spin />;

    default:
      assertUnreachable(variant);
      return null;
  }
};

const MultiStateItemRow: React.FC<{ item: ToastMultiStateItem }> = ({
  item,
}) => (
  <div className="flex items-center gap-x-2 font-inter-normal-12px text-gray-500">
    <span className="self-start">
      <ToastIcon variant={item.variant} />
    </span>
    {item.description}
  </div>
);

const isUrgentVariant = (variant: NotificationVariant) =>
  variant === "error" || variant === "warning";

export const ToastContent: React.FC<ContentProps> = ({
  isDismissible = true,
  variant,
  title,
  description,
  items,
  actionText,
  onActionClick,
  onDismiss,
}) => (
  <div
    className="flex max-h-96 w-[365px] gap-x-2 rounded-2xl bg-white p-5 shadow-lg"
    data-loc={`${variant}-notification`}
    data-record="true"
    role={isUrgentVariant(variant) ? "alert" : "status"}
    aria-live={isUrgentVariant(variant) ? "assertive" : "polite"}
  >
    <ToastIcon variant={variant} />
    <div className="flex w-full flex-grow flex-col gap-y-2">
      <div className="flex items-start justify-between">
        <p
          className="break-words font-inter-semibold-16px text-gray-800"
          data-record="true"
        >
          {title}
        </p>
        {isDismissible && (
          <IconButton
            dataLoc="notification-close"
            icon={faXmark}
            size="sm"
            onClick={onDismiss}
          />
        )}
      </div>

      {description && (
        <div className="font-inter-normal-12px text-gray-500">
          <ClampedContent>{description}</ClampedContent>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="flex flex-col gap-y-2">
          {items.map((item, index) => (
            <MultiStateItemRow key={index} item={item} />
          ))}
        </div>
      )}

      {actionText && (
        <button
          className="text-left font-inter-semibold-12px text-gray-800 hover:underline"
          onClick={onActionClick}
        >
          {actionText}
        </button>
      )}
    </div>
  </div>
);

/**
 * Toast component
 * NOTE: Avoid using this component directly, use helpers from utils
 * Figma: https://www.figma.com/design/BO9DWO8PKNGNSI3HhpSobA/Design-System-v2?node-id=9503-2702&node-type=text&m=dev
 */
export const Toast: React.FC<Props> = ({
  toastInstance,
  isDismissible = true,
  variant,
  title,
  description,
  items,
  actionText,
  onActionClick,
  onDismiss,
}) => {
  useTrackNotification({ variant, title, description });

  const handleDismiss = () => {
    toast.dismiss(toastInstance.id);
    onDismiss?.();
  };

  const sharedProps = {
    isDismissible,
    variant,
    title,
    description,
    items,
    onDismiss: handleDismiss,
  } as const;

  return (
    <ToastBar style={RESET_TOASTBAR_STYLES} toast={toastInstance}>
      {() =>
        actionText ? (
          <ToastContent
            {...sharedProps}
            actionText={actionText}
            onActionClick={onActionClick}
          />
        ) : (
          <ToastContent {...sharedProps} />
        )
      }
    </ToastBar>
  );
};
